import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Tooltip } from './overlay';

export const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors duration-fast ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover',
        secondary: 'bg-primary-soft text-primary hover:bg-primary/15',
        outline: 'border border-border-strong/60 bg-surface text-text hover:bg-surface-sunken',
        ghost: 'text-text hover:bg-surface-sunken',
        danger: 'bg-negative text-primary-foreground shadow-sm hover:bg-negative/90',
        accent: 'bg-accent text-accent-foreground shadow-sm hover:bg-accent/90',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm coarse:min-h-touch',
        md: 'h-10 px-4 text-sm coarse:min-h-touch',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10 coarse:size-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element (e.g. a router `<Link>`) with button styles. */
  readonly asChild?: boolean;
  /** Shows a spinner, disables the button and sets `aria-busy`. */
  readonly loading?: boolean;
  /** Icon before the label (lucide element). */
  readonly icon?: ReactNode;
  /** Icon after the label. */
  readonly iconRight?: ReactNode;
}

/** Primary action element. Variants: primary · secondary · outline · ghost · danger · accent · link. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, icon, iconRight, children, disabled, type, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className);
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : icon}
        {children}
        {iconRight}
      </button>
    );
  },
);
Button.displayName = 'Button';

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'iconRight' | 'children' | 'aria-label'> {
  /** Required accessible name; also shown as a tooltip unless `tooltip={false}`. */
  readonly 'aria-label': string;
  readonly icon: ReactNode;
  readonly tooltip?: boolean;
}

/** Square icon-only button. `aria-label` is required and doubles as the tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, tooltip = true, variant = 'ghost', size = 'icon', ...props }, ref) => {
    const btn = (
      <Button ref={ref} variant={variant} size={size} {...props}>
        {icon}
      </Button>
    );
    return tooltip ? <Tooltip content={props['aria-label']}>{btn}</Tooltip> : btn;
  },
);
IconButton.displayName = 'IconButton';
